import type { SceneNode } from "../scene/model";
import type { NativeSession } from "../native/session";
import { effects } from "../native/effects";
import {
  changeShadow,
  shadowStyle,
  shadowAngle,
  shadowDistance,
  type ShadowField,
} from "../native/shadow";
import "./native-shadow-controls.css";
import { NativeShadowCompact } from "./native-shadow-compact";

/** A single source of shadow controls, movable between the inspector and compact popup. */
export class NativeShadowControls {
  readonly element = document.createElement("div");
  readonly compact: NativeShadowCompact;
  private selection = "";
  private controls = new Map<ShadowField, HTMLInputElement[]>();
  private edit: {
    input: HTMLInputElement;
    nodes: SceneNode[];
    generation: number;
    revision: number;
  } | null = null;
  private blocked: HTMLInputElement | null = null;
  private settings = document.createElement("div");
  constructor(
    private session: NativeSession,
    private changed: () => void,
    private prepare: () => boolean,
  ) {
    this.element.id = "native-shadow-editor";
    this.element.innerHTML =
      '<label class="shape-property-row"><input type="checkbox" id="show-shadow" aria-controls="native-shadow-settings"><span>Тень</span></label>';
    this.settings.id = "native-shadow-settings";
    this.settings.hidden = true;
    this.settings.innerHTML =
      '<label class="native-shadow-heading">Цвет тени <input id="native-shadow-color" type="color" value="#0f172a" data-opaque-only="true" data-display-alpha="1" aria-label="Цвет тени"></label>';
    this.element.append(this.settings);
    this.bind("color", this.settings.querySelector("input")!);
    for (const [field, label, max, step] of [
      ["blur", "Размер (размытие), px", 200, 1],
      ["alpha", "Непрозрачность, %", 100, 1],
      ["distance", "Расстояние, px", 1000, 1],
      ["angle", "Направление, °", 360, 1],
    ] as const) {
      const wrapper = document.createElement("div");
      wrapper.className = "native-shadow-range";
      wrapper.innerHTML = `<label class="native-shadow-heading" for="native-shadow-${field}"><span>${label}</span><input id="native-shadow-${field}" type="number" min="0" max="${max}" step="${step}" placeholder="Разные"></label><input id="native-shadow-${field}-range" type="range" min="0" max="${max}" step="${step}" aria-label="${label}">`;
      this.settings.append(wrapper);
      wrapper
        .querySelectorAll<HTMLInputElement>("input")
        .forEach((input) => this.bind(field, input));
    }
    const hint = document.createElement("p");
    hint.textContent = "0° — вправо, 90° — вниз";
    hint.className = "native-muted";
    this.settings.append(hint);
    this.compact = new NativeShadowCompact(
      (field, input) => this.bind(field, input),
      () => this.finish(true),
    );
    document.addEventListener(
      "pointerdown",
      (event) => {
        const target = event.target as HTMLElement;
        if (
          this.edit &&
          target !== this.edit.input &&
          !(
            this.edit.input.type === "color" &&
            target.closest("#irl-color-picker")
          )
        )
          this.finish(true);
      },
      true,
    );
    window.addEventListener("pointerup", () => {
      if (this.edit?.input.type === "range")
        queueMicrotask(() => this.finish(true));
    });
    window.addEventListener("blur", () => this.finish(false));
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.finish(false);
    });
    window.addEventListener(
      "keydown",
      (event) => {
        if (
          event.key === "Escape" &&
          !document.querySelector("#irl-color-picker:not([hidden])")
        ) {
          if (this.active) this.finish(false);
          else if (this.compact.dismissPopup()) {
            /* Keep the tool row open. */
          } else if (this.compact.opened) this.compact.close();
          else return;
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true,
    );
  }
  get active() {
    return !!this.edit;
  }
  private valid() {
    return (
      this.edit?.generation === this.session.generation &&
      this.edit.revision === this.session.revision &&
      this.session.gesturing
    );
  }
  private bind(field: ShadowField, input: HTMLInputElement) {
    this.controls.set(field, [...(this.controls.get(field) ?? []), input]);
    input.onpointerdown = () => {
      this.blocked = null;
    };
    input.onkeydown = (e) => {
      if (e.key !== "Escape") this.blocked = null;
      if (e.key === "Enter") {
        this.finish(true);
        input.blur();
      }
    };
    input.oninput = () => this.preview(field, input);
    input.onchange = () => {
      this.preview(field, input);
      this.finish(true);
    };
    input.onblur = () => this.finish(true);
    input.onpointercancel = () => this.finish(false);
  }
  private preview(field: ShadowField, input: HTMLInputElement) {
    if (this.blocked === input || !input.value.trim()) return;
    const value = field === "color" ? input.value : Number(input.value);
    if (typeof value === "number" && !Number.isFinite(value)) return;
    if (this.edit?.input !== input) {
      this.finish(true);
      if (!this.prepare()) return;
      const nodes = this.session.selectedNodes.filter((n) => effects(n).shadow);
      if (!nodes.length) return;
      this.edit = {
        input,
        nodes,
        generation: this.session.generation,
        revision: this.session.revision,
      };
      this.session.begin();
    }
    if (!this.valid()) {
      this.edit = null;
      return;
    }
    this.session.replaceMany(changeShadow(this.edit!.nodes, field, value));
    this.changed();
  }
  finish(apply: boolean) {
    const edit = this.edit;
    if (!edit) return;
    if (!apply) this.blocked = edit.input;
    if (this.valid()) {
      if (apply) this.session.finish();
      else this.session.cancel();
    }
    this.edit = null;
    this.render(true);
    this.changed();
  }
  render(force = false) {
    if (this.edit && !this.valid()) this.edit = null;
    const nodes = this.session.selectedNodes;
    const selection =
      this.session.generation + ":" + nodes.map((n) => n.id).join();
    if (selection !== this.selection) {
      this.compact.close();
      this.selection = selection;
    }
    this.settings.hidden =
      !nodes.length || !nodes.every((n) => effects(n).shadow);
    this.element
      .querySelector("#show-shadow")!
      .setAttribute("aria-expanded", String(!this.settings.hidden));
    if (this.settings.hidden) {
      this.compact.close();
      return;
    }
    for (const [field, inputs] of this.controls) {
      const values = nodes.map((node) => {
        const s = shadowStyle(node);
        return field === "color"
          ? s.color.toLowerCase()
          : Math.round(
              (field === "angle"
                ? shadowAngle(s)
                : field === "distance"
                  ? shadowDistance(s)
                  : field === "alpha"
                    ? s.alpha * 100
                    : s.blur) * 100,
            ) / 100;
      });
      const mixed = values.some((v) => v !== values[0]);
      if (field === "color") this.compact.color(String(values[0]));
      for (const input of inputs) {
        if (force || document.activeElement !== input)
          input.value =
            mixed && input.type === "number" ? "" : String(values[0]);
        input.dataset.mixed = String(mixed);
        if (input.type === "range")
          input.setAttribute(
            "aria-valuetext",
            mixed ? "Разные значения" : String(values[0]),
          );
        if (field === "color")
          input.title = mixed ? "Разные цвета тени" : "Цвет тени";
      }
    }
  }
}
